import React, {useCallback, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import clsx from 'clsx';
import AdminService from '@/modules/admin/services/adminService';
import {getBaseDomain} from '@/shared/utils/domain';
import {getAdminHost} from '@/shared/utils/adminHost';
import type {
    GlobalAdminOverviewResponse,
    GlobalAdminRange,
    GlobalAdminMetric,
    GlobalAdminOrgSort,
    GlobalAdminOrganizationRow,
} from '@/shared/types/api';

const RANGE_OPTIONS: Array<{label: string; value: GlobalAdminRange}> = [
    {label: 'Last 24 Hours', value: '24h'},
    {label: 'Last 7 Days', value: '7d'},
    {label: 'Last 30 Days', value: '30d'},
    {label: 'Last 90 Days', value: '90d'},
];

const METRIC_OPTIONS: Array<{label: string; value: GlobalAdminMetric}> = [
    {label: 'Orders', value: 'orders'},
    {label: 'Patients', value: 'patients'},
    {label: 'Results', value: 'results'},
    {label: 'Appointments', value: 'appointments'},
    {label: 'Kits', value: 'kits'},
    {label: 'Active Orgs', value: 'active_orgs'},
    {label: 'Revenue (Coming Soon)', value: 'revenue'},
];

const SORT_OPTIONS: Array<{label: string; value: GlobalAdminOrgSort}> = [
    {label: 'Orders', value: 'orders'},
    {label: 'Patients', value: 'patients'},
    {label: 'Results', value: 'results'},
    {label: 'Appointments', value: 'appointments'},
    {label: 'Kits', value: 'kits'},
    {label: 'Revenue (Coming Soon)', value: 'revenue'},
];

const formatNumber = (value: number) => value.toLocaleString('en-US');

const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });

const formatDelta = (value: number) => {
    if (value === 0) return '0';
    return value > 0 ? `+${formatNumber(value)}` : formatNumber(value);
};

const MetricCard: React.FC<{
    label: string;
    value: string;
    delta?: number | null;
    helper?: string;
}> = ({label, value, delta, helper}) => {
    const deltaValue = typeof delta === 'number' ? delta : null;
    const deltaLabel = deltaValue == null ? 'N/A' : formatDelta(deltaValue);
    const deltaTone =
        deltaValue == null
            ? 'text-mld-muted-text'
            : deltaValue > 0
                ? 'text-mld-success'
                : deltaValue < 0
                    ? 'text-mld-danger'
                    : 'text-mld-muted-text';

    return (
        <div className="rounded-xl border border-mld-panel-border border-l-4 border-l-gray-200 bg-white p-4 shadow-sm transition hover:border-l-mld-primary">
            <div className="text-[11px] uppercase tracking-wide text-mld-muted-text">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-mld-text-primary">{value}</div>
            <div className="mt-2 flex items-center justify-between text-xs">
                <span className={clsx(deltaTone)}>{deltaLabel}</span>
                {helper ? <span className="text-mld-muted-text">{helper}</span> : null}
            </div>
        </div>
    );
};

export const GlobalOverview: React.FC = () => {
    const [range, setRange] = useState<GlobalAdminRange>('24h');
    const [metric, setMetric] = useState<GlobalAdminMetric>('orders');
    const [sort, setSort] = useState<GlobalAdminOrgSort>('orders');

    const overviewQuery = useQuery({
        queryKey: ['admin-global-overview', range],
        queryFn: () => AdminService.getGlobalOverview(range),
    });

    const orgsQuery = useQuery({
        queryKey: ['admin-global-orgs', range, sort],
        queryFn: () => AdminService.getGlobalOrganizations(range, sort, 20),
    });

    const overview = overviewQuery.data as GlobalAdminOverviewResponse | undefined;
    const totals = overview?.totals;
    const deltas = overview?.deltas;

    const chartData = useMemo(() => {
        const series = overview?.series ?? [];
        return series.map((point) => {
            const date = new Date(point.ts);
            const label = range === '24h'
                ? date.toLocaleString('en-US', {hour: 'numeric', hour12: true, month: 'short', day: 'numeric'})
                : date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});

            const value = (() => {
                switch (metric) {
                    case 'orders':
                        return point.orders;
                    case 'results':
                        return point.results;
                    case 'patients':
                        return point.patients;
                    case 'appointments':
                        return point.appointments;
                    case 'kits':
                        return point.kits;
                    case 'active_orgs':
                        return point.active_orgs;
                    case 'revenue':
                        return point.revenue ?? 0;
                    default:
                        return 0;
                }
            })();

            return {
                label,
                value,
            };
        });
    }, [overview?.series, range, metric]);

    const topOrgs = (orgsQuery.data ?? []) as GlobalAdminOrganizationRow[];

    const handleViewOrg = useCallback(async (org: GlobalAdminOrganizationRow) => {
        if (typeof window === 'undefined') return;
        try {
            const response = await AdminService.createOrgSwitchToken(org.id);
            const baseDomain = getBaseDomain(window.location.hostname);
            const portSuffix = window.location.port ? `:${window.location.port}` : '';
            const targetHost = `${org.subdomain}.${baseDomain}${portSuffix}`;
            const adminHost = getAdminHost(window.location.hostname);
            const isDashboardTarget = `${org.subdomain}.${baseDomain}` === adminHost;
            const redirectPath = isDashboardTarget ? '/admin?orgConsole=1' : '/admin';

            await AdminService.exchangeOrgSwitchToken(response.token);
            const targetUrl = `${window.location.protocol}//${targetHost}${redirectPath}`;
            window.location.assign(targetUrl);
        } catch (error) {
            console.error('Failed to open org console', error);
        }
    }, []);

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap gap-4 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-mld-muted-text">Range</label>
                    <select
                        value={range}
                        onChange={(event) => setRange(event.target.value as GlobalAdminRange)}
                        className="rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-mld-text-secondary">
                        {RANGE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-mld-muted-text">Chart Metric</label>
                    <select
                        value={metric}
                        onChange={(event) => setMetric(event.target.value as GlobalAdminMetric)}
                        className="rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-mld-text-secondary">
                        {METRIC_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-mld-muted-text">Top Orgs Sort</label>
                    <select
                        value={sort}
                        onChange={(event) => setSort(event.target.value as GlobalAdminOrgSort)}
                        className="rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-mld-text-secondary">
                        {SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {overviewQuery.isLoading ? (
                <div className="rounded-xl border border-dashed border-mld-panel-border bg-white p-8 text-center text-mld-muted-text">
                    Loading global overview...
                </div>
            ) : overviewQuery.isError ? (
                <div className="rounded-xl border border-dashed border-mld-danger/20 bg-mld-danger/5 p-8 text-center text-mld-danger">
                    Failed to load global overview.
                </div>
            ) : (
                <>
                    <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
                        <MetricCard
                            label="Orders"
                            value={formatNumber(totals?.orders ?? 0)}
                            delta={deltas?.orders ?? 0}
                        />
                        <MetricCard
                            label="Patients"
                            value={formatNumber(totals?.patients ?? 0)}
                            delta={deltas?.patients ?? 0}
                        />
                        <MetricCard
                            label="Results"
                            value={formatNumber(totals?.results ?? 0)}
                            delta={deltas?.results ?? 0}
                        />
                        <MetricCard
                            label="Appointments"
                            value={formatNumber(totals?.appointments ?? 0)}
                            delta={deltas?.appointments ?? 0}
                        />
                        <MetricCard
                            label="Kits"
                            value={formatNumber(totals?.kits ?? 0)}
                            delta={deltas?.kits ?? 0}
                        />
                        <MetricCard
                            label="Active Orgs"
                            value={formatNumber(totals?.active_orgs ?? 0)}
                            delta={deltas?.active_orgs ?? 0}
                            helper="Period"
                        />
                        <MetricCard
                            label="Total Orgs"
                            value={formatNumber(overview?.total_orgs ?? 0)}
                            helper="Active in system"
                        />
                        <MetricCard
                            label="Revenue"
                            value={totals?.revenue == null ? 'N/A' : formatCurrency(totals.revenue)}
                            delta={null}
                            helper="Coming soon"
                        />
                    </div>

                    <div className="rounded-xl border border-mld-panel-border bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Global Trend</h3>
                            <span className="text-xs text-mld-muted-text">{RANGE_OPTIONS.find((opt) => opt.value === range)?.label}</span>
                        </div>
                        <div className="mt-4 h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{top: 10, right: 20, left: 0, bottom: 0}}>
                                    <defs>
                                        <linearGradient id="globalTrend" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="label" tick={{fontSize: 12}} interval="preserveStartEnd" />
                                    <YAxis tick={{fontSize: 12}} />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 12,
                                            borderColor: '#e5e7eb',
                                            fontSize: 12,
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#2563eb"
                                        strokeWidth={2}
                                        fill="url(#globalTrend)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="rounded-xl border border-mld-panel-border bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Top Organizations</h3>
                            <span className="text-xs text-mld-muted-text">Sorted by {sort.replace('_', ' ')}</span>
                        </div>
                        {orgsQuery.isLoading ? (
                            <div className="py-6 text-sm text-mld-muted-text">Loading organizations...</div>
                        ) : orgsQuery.isError ? (
                            <div className="py-6 text-sm text-mld-danger">Failed to load organizations.</div>
                        ) : topOrgs.length === 0 ? (
                            <div className="py-6 text-sm text-mld-muted-text">No organizations found for this range.</div>
                        ) : (
                            <div className="mt-4 overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-mld-menu-background text-left text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                        <tr>
                                            <th className="px-4 py-3">Organization</th>
                                            <th className="px-4 py-3">Orders</th>
                                            <th className="px-4 py-3">Patients</th>
                                            <th className="px-4 py-3">Results</th>
                                            <th className="px-4 py-3">Appointments</th>
                                            <th className="px-4 py-3">Kits</th>
                                            <th className="px-4 py-3">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topOrgs.map((org) => (
                                            <tr key={org.id} className="border-b border-mld-panel-border">
                                                <td className="px-4 py-3">
                                                    <div className="font-medium text-mld-text-primary">
                                                        {org.display_name || org.name}
                                                    </div>
                                                    <div className="text-xs text-mld-muted-text">{org.subdomain}</div>
                                                </td>
                                                <td className="px-4 py-3 text-mld-text-secondary">{formatNumber(org.orders)}</td>
                                                <td className="px-4 py-3 text-mld-text-secondary">{formatNumber(org.patients)}</td>
                                                <td className="px-4 py-3 text-mld-text-secondary">{formatNumber(org.results)}</td>
                                                <td className="px-4 py-3 text-mld-text-secondary">{formatNumber(org.appointments)}</td>
                                                <td className="px-4 py-3 text-mld-text-secondary">{formatNumber(org.kits)}</td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        type="button"
                                                        className="rounded-lg border border-mld-panel-border px-3 py-1 text-xs font-medium text-mld-text-secondary hover:border-blue-400 hover:text-mld-info"
                                                        onClick={() => handleViewOrg(org)}>
                                                        View Org
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default GlobalOverview;
